import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import { Input } from '@crazyegginc/hatch';

import { AUDIENCE_METADATA } from '../../../metadata';
import { ConditionTooltip } from '../ConditionTooltip';

export function NumberValue({ index, innerIndex }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext();
  const condition = values.conditions[index];
  const { criteria, rows } = condition;
  const row = rows[innerIndex];
  const valuePath = `conditions[${index}].rows[${innerIndex}].values`;
  const isBetween = row.comparison === 'between';
  const showTooltip = row.tooltip === 'value';

  const metadata = AUDIENCE_METADATA[criteria];
  const rowValues = row.values;
  const rowTouched = touched.conditions?.[index]?.rows?.[innerIndex]?.values;
  const rowError = errors.conditions?.[index]?.rows?.[innerIndex]?.values;

  function onTooltipDismiss() {
    setFieldValue(`conditions[${index}].rows[${innerIndex}].tooltip`, false, false);
  }

  function dismissTooltip() {
    if (showTooltip) {
      onTooltipDismiss();
    }
  }

  function onValueChange(event) {
    dismissTooltip();
    handleChange(event);
  }

  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-2.5">
      {(isBetween ? ['amount', 'max'] : ['amount']).map((fieldName, i, fields) => {
        const isMax = fieldName === 'max';
        const fieldError = getVisibleFieldError({ rowTouched, rowError, isBetween, fieldName });
        const input = (
          <div className="w-[80px]">
            <Input
              id={`${valuePath}.${fieldName}`}
              name={`${valuePath}.${fieldName}`}
              value={rowValues[fieldName]}
              placeholder={isBetween ? (isMax ? 'Max' : 'Min') : metadata.placeholder}
              onChange={onValueChange}
              onBlur={handleBlur}
              aria-label={isBetween ? `${criteria} ${isMax ? 'maximum' : 'minimum'}` : criteria}
              className="!pl-2.5"
              error={fieldError}
            />
          </div>
        );

        return (
          <Fragment key={fieldName}>
            <ConditionTooltip
              criteria={metadata}
              show={showTooltip && i === fields.length - 1}
              onDismiss={onTooltipDismiss}
            >
              {input}
            </ConditionTooltip>
            <div className="last:hidden flex h-[35px] items-center">and</div>
          </Fragment>
        );
      })}
    </div>
  );
}

function getVisibleFieldError({ rowTouched, rowError, isBetween, fieldName }) {
  // String range errors describe the pair, so either touched bound can reveal them.
  if (isBetween && typeof rowError === 'string') {
    const anyRangeTouched = typeof rowTouched === 'boolean' ? rowTouched : !!rowTouched?.amount || !!rowTouched?.max;
    return anyRangeTouched ? rowError : null;
  }

  // Field-specific errors should wait until their own input has been touched.
  const isTouched = typeof rowTouched === 'boolean' ? rowTouched : !!rowTouched?.[fieldName];
  if (!isTouched || !rowError) return null;

  if (!isBetween) {
    return typeof rowError === 'string' ? rowError : (rowError.amount ?? null);
  }

  const minError = rowError.amount ?? null;
  const maxError = rowError.max ?? null;

  // Show one range error at a time to avoid overlapping messages in the UI.
  if (minError) {
    return fieldName === 'amount' ? minError : null;
  }

  if (maxError) {
    return fieldName === 'max' ? maxError : null;
  }

  return null;
}
